import { ElementType } from 'domelementtype'
import { Element as DomElement, Node as DomNode, Text } from 'domhandler'
import { appendChild, prependChild, removeElement } from 'domutils'
import { attributesToProps, domToReact } from 'html-react-parser'
import React from 'react'

const ALLOW_STYLE: string[] = ['width']

const filterTableStyle = (style: string) => {
  const parts = style.split(';')
  const filteredStyle: string[] = []
  for (let i = 0; i < parts.length; i++) {
    if (
      ALLOW_STYLE.indexOf(parts[i].split(':')[0].toLowerCase().trim()) !== -1
    ) {
      filteredStyle.push(parts[i])
    }
  }
  return `${filteredStyle.join(';')};`
}

const getLimitCharacters = (
  domNode: DomNode,
  limit: number
): { child: DomNode; leftLimit: number } => {
  if (domNode instanceof Text) {
    domNode.data = domNode.data.slice(0, limit)
    return { child: domNode, leftLimit: limit - domNode.data.length }
  }
  let remainLimit = limit
  if (domNode instanceof DomElement) {
    for (let i = 0; i < domNode.children.length - 1; i += 1) {
      const { leftLimit } = getLimitCharacters(domNode.children[i], remainLimit)
      remainLimit = leftLimit
      if (remainLimit <= 0) {
        domNode.children = domNode.children.slice(0, i + 1)
        break
      }
    }
  }
  return { child: domNode, leftLimit: remainLimit }
}

const getLongestTextLength = (domNode: DomElement): number => {
  let longestLength = 0
  if ('children' in domNode && domNode.children.length > 0) {
    domNode.children.forEach((child: DomNode) => {
      if (child instanceof DomElement && child.name === 'p') {
        const getPLength = (pNode: DomElement): number => {
          let pLength = 0
          pNode.children.forEach((child: DomNode) => {
            if (child instanceof Text) {
              pLength += child.data.length
            } else if (child instanceof DomElement) {
              pLength += getPLength(child)
            }
          })
          return pLength
        }
        longestLength = Math.max(longestLength, getPLength(child))
      } else if (child instanceof Text) {
        longestLength =
          child.data.length > longestLength ? child.data.length : longestLength
      }
    })
  }
  return longestLength
}

function stackTable(table: DomElement, options: any) {
  let thead: any = null
  let tbody: any = null

  table.children.forEach((tpart) => {
    if (tpart instanceof DomElement) {
      thead = tpart.name === 'thead' ? tpart : thead
      tbody = tpart.name === 'tbody' ? tpart : tbody
    }
  })
  try {
    const mobileStackTables: DomElement[] = []
    const thNode = thead?.children[0] as DomElement

    for (let i = 0; i < tbody.children.length; i++) {
      const stackTable = new DomElement(
        'table',
        { class: 'wp-table-mobile' },
        undefined,
        ElementType.Tag
      )
      const mobileTbody = new DomElement(
        'tbody',
        {},
        undefined,
        ElementType.Tag
      )
      appendChild(stackTable, mobileTbody)
      mobileStackTables.push(stackTable)
      for (let i = 0; i < thNode.children.length; i++) {
        if (thNode.children[i] instanceof DomElement) {
          const colspan = (thNode.children[i] as DomElement).attribs.colspan
          const stackRowNode = new DomElement(
            'tr',
            {},
            undefined,
            ElementType.Tag
          )
          appendChild(mobileTbody, stackRowNode)
          const stackRowHeadNode = thNode.children[i].cloneNode(
            true
          ) as DomElement
          stackRowHeadNode.attribs.scope = 'row'
          if (colspan) {
            for (let j = 1; j < Number(colspan); j++) {
              appendChild(mobileTbody, stackRowNode.cloneNode())
            }
            stackRowHeadNode.attribs.rowspan = colspan
            stackRowHeadNode.attribs.colspan = '1'
          }
          appendChild(stackRowNode, stackRowHeadNode)
        }
      }

      const trNode = tbody.children[i] as DomElement
      for (let j = 0; j < trNode.children.length; j++) {
        const stackTdNode = trNode.children[j].cloneNode(true) as DomElement
        stackTdNode.attribs.style = ''
        appendChild(mobileTbody.children[j] as DomElement, stackTdNode)
      }
    }
    return <>{domToReact(mobileStackTables, options)}</>
  } catch (e) {
    return (
      <table
        {...attributesToProps(table.attribs)}
        style={{}}
        className="wp-table-mobile"
      >
        {domToReact(table.children, options)}
      </table>
    )
  }
}

export default function adjustDesktopTable(table: DomElement, options: any) {
  let thead: any = null
  let tbody: any = null
  table.children.forEach((tpart) => {
    if (tpart instanceof DomElement) {
      thead = tpart.name === 'thead' ? tpart : thead
      tbody = tpart.name === 'tbody' ? tpart : tbody
    }
  })
  if (!thead && !tbody)
    return (
      <table
        {...attributesToProps(table.attribs)}
        className="wp-table-no-mobile"
      >
        {domToReact(table.children, options)}
      </table>
    )
  let isFixedTable = true

  if (!thead) {
    thead = new DomElement('thead', {}, undefined, ElementType.Tag)
    appendChild(thead, tbody.children[0].cloneNode(true))
    prependChild(table, thead)
    removeElement(tbody.children[0])
  }

  tbody.children?.forEach((node: DomNode) => {
    if (!(node instanceof DomElement)) {
      return
    }

    if (node.name === 'tr') {
      node.attribs.style = ''

      node.children.forEach((node: DomNode) => {
        if (!(node instanceof DomElement)) {
          return
        }
        let tableCellStyle = ''
        const longestTextLength = getLongestTextLength(node)
        if (longestTextLength > 40) {
          tableCellStyle = 'text-align: left;'
        }
        getLimitCharacters(node, 120)
        const filteredTdStyle = filterTableStyle(node.attribs.style ?? '')
        if (filteredTdStyle !== '') {
          isFixedTable = false
        }
        node.attribs.style = `${filterTableStyle(
          node.attribs.style ?? ''
        )}${tableCellStyle}`
      })
    }
  })
  thead.children.forEach((node: DomNode) => {
    if (!(node instanceof DomElement)) {
      return
    }
    if (node.name === 'tr') {
      node.children.forEach((node: DomNode) => {
        if (!(node instanceof DomElement)) {
          return
        }
        node.name = 'th'
        node.attribs.scope = 'col'
      })
    }
  })
  return (
    <>
      <table
        {...attributesToProps(table.attribs)}
        style={{ tableLayout: isFixedTable ? 'fixed' : 'auto' }}
        className="wp-table-no-mobile"
      >
        {domToReact([thead, tbody], options)}
      </table>
      {stackTable(table, {})}
    </>
  )
}
